import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { STATIC } from '../staticMode';

const TOKEN_KEY = 'seediq_token';
const AuthContext = createContext(null);

// Static build: no accounts, no backend. One anonymous local user so the app renders
// (the Login gate is bypassed); auth actions are no-ops. Data lives in localStorage.
const STATIC_VALUE = {
  token: 'local', user: { id: 'local', name: '' }, ready: true,
  register: async () => {}, login: async () => {}, logout: () => {},
};

// One-time migration of this device's old localStorage data into a new account.
async function migrateLocalData(token) {
  try {
    const vocab = JSON.parse(localStorage.getItem('seediq_vocab') || '{}');
    if (Object.keys(vocab).length) await api('/vocab/bulk', { method: 'POST', token, body: vocab });
    const progress = JSON.parse(localStorage.getItem('seediq_progress') || '{}');
    if (Object.keys(progress).length) await api('/progress/bulk', { method: 'POST', token, body: progress });
  } catch { /* best effort */ }
}

export function AuthProvider({ children }) {
  if (STATIC) return <AuthContext.Provider value={STATIC_VALUE}>{children}</AuthContext.Provider>;
  return <BackendAuthProvider>{children}</BackendAuthProvider>;
}

function BackendAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [user, setUser]   = useState(null);
  const [ready, setReady] = useState(false);

  // Validate a stored token on load
  useEffect(() => {
    if (!token) { setUser(null); setReady(true); return; }
    let alive = true;
    api('/auth/me', { token })
      .then(u => { if (alive) { setUser(u); setReady(true); } })
      .catch(() => { if (alive) { localStorage.removeItem(TOKEN_KEY); setToken(null); setUser(null); setReady(true); } });
    return () => { alive = false; };
  }, [token]);

  const register = useCallback(async (name, passphrase) => {
    const r = await api('/auth/register', { method: 'POST', body: { name, passphrase } });
    await migrateLocalData(r.token);
    localStorage.setItem(TOKEN_KEY, r.token);
    setToken(r.token); setUser({ id: r.id, name: r.name });
  }, []);

  const login = useCallback(async (name, passphrase) => {
    const r = await api('/auth/login', { method: 'POST', body: { name, passphrase } });
    localStorage.setItem(TOKEN_KEY, r.token);
    setToken(r.token); setUser({ id: r.id, name: r.name });
  }, []);

  const logout = useCallback(() => {
    if (token) api('/auth/logout', { method: 'POST', token }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setToken(null); setUser(null);
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, user, ready, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
